import "phaser/plugins/spine/dist/SpinePlugin.js";
import OutlinePipelinePlugin from "phaser3-rex-plugins/plugins/outlinepipeline-plugin.js";
import LoadBatchManager from "./core/LoadBatchManager";
import MusicManager from "./core/MusicManager";
import { GameObjectFactoryPlugin } from "./core/overrides/GameObjectFactoryPlugin";
import ImageOverride from "./core/overrides/ImageOverride";
import { LoaderPlugin } from "./core/overrides/LoaderPlugin";
import PhaserOverrides from "./core/overrides/PhaserOverrides";
import QualityManager from "./core/QualityManager";
import RotateDevice from "./core/RotateDevice";
import ScaleManager, { OrientationType, ScaleBy } from "./core/ScaleManager";
import SoundManager from "./core/SoundManager";
import BootScene from "./scenes/BootScene";
import GameCompleteScene from "./scenes/GameCompleteScene";
import GameScene from "./scenes/GameScene";
import LevelSelectScene from "./scenes/LevelSelectScene";
import PreloaderScene from "./scenes/PreloaderScene";
import ScreenTransition from "./scenes/ScreenTransition";
import SplashScene from "./scenes/SplashScene";
import SessionData from "./SessionData";
import Net from "./utils/Net";

export default class F84Game extends Phaser.Game {
	private _defaultWidth: number;
	private _defaultHeight: number;

	public get DefaultWidth(): number {
		return this._defaultWidth;
	}

	public get DefaultHeight(): number {
		return this._defaultHeight;
	}

	public get DevicePixelRatio(): number {
		return Math.min(window.devicePixelRatio, 2); //Max out allowed DPR at 2 for performance reasons
	}

	private _scaleManager: ScaleManager;
	public get ScaleManager(): ScaleManager {
		return this._scaleManager;
	}

	private _quality: QualityManager;
	public get quality(): QualityManager {
		return this._quality;
	}

	private static _instance: F84Game;
	public static get Instance(): F84Game {
		return this._instance;
	}

	private _sessionData: SessionData;
	public get SessionData(): SessionData {
		return this._sessionData;
	}

	private _locale = "en-us";
	public get locale(): string {
		return this._locale;
	}

	private _soundManager: SoundManager;
	public get soundManager(): SoundManager {
		return this._soundManager;
	}

	private _music: MusicManager;
	public get music(): MusicManager {
		return this._music;
	}

	private _loadBatches: LoadBatchManager;
	public get loadBatches(): LoadBatchManager {
		return this._loadBatches;
	}

	public getBounds(): Phaser.Geom.Rectangle {
		return new Phaser.Geom.Rectangle(0, 0, this.scale.width, this.scale.height);
	}

	public get allowTouchControls(): boolean {
		return !this.device.os.desktop || this.device.input.touch;
	}

	private _stats: Stats = null;
	enableStats(): void {
		if (this._stats == null) {
			this._stats = new Stats();
			window.document.body.appendChild(this._stats.dom);
			this._stats.dom.style.cssText = "position:fixed;bottom:0;left:0;cursor:pointer;opacity:0.9;z-index:10000";
		}
	}

	private _lowQuality = false;
	public lowQuality(): boolean {
		return this._lowQuality;
	}
	public get qualitySuffix(): string {
		return this._lowQuality ? "-lq" : "";
	}

	boot(): void {
		super.boot();
	}

	constructor(
		constructedCanvas: HTMLCanvasElement,
		constructedContext: CanvasRenderingContext2D,
		width: number,
		height: number,
		orientation: OrientationType
	) {
		super({
			type: Phaser.WEBGL,
			width: width,
			height: height,
			canvas: constructedCanvas,
			context: constructedContext,
			physics: {
				default: "arcade",
				arcade: {
					gravity: { y: 300 },
					debug: false,
				},
			},
			input: {
				activePointers: 1,
			},
			scale: {
				autoCenter: Phaser.Scale.NO_CENTER,
				mode: Phaser.Scale.NONE,
			},
			plugins: {
				global: [
					{ key: "LoaderPlugin", plugin: LoaderPlugin, start: true },
					{ key: "GameObjectFactoryPlugin", plugin: GameObjectFactoryPlugin, start: true },
					{ key: "rexOutlinePipeline", plugin: OutlinePipelinePlugin, start: true },
				],
				scene: [{ key: "SpinePlugin", plugin: window.SpinePlugin, mapping: "spine" }],
			},
		});

		this._defaultWidth = width;
		this._defaultHeight = height;

		F84Game._instance = this;

		this._sessionData = new SessionData();

		this._quality = new QualityManager(this);

		ImageOverride.override(this._quality.resolution); //serve half res graphics on low quality devices
		PhaserOverrides.override();

		this._music = new MusicManager(this, 0.833);
		this._soundManager = new SoundManager(this, 0.833);

		this._loadBatches = new LoadBatchManager();

		this._scaleManager = new ScaleManager(this, orientation, ScaleBy.HEIGHT);

		this._lowQuality = window.innerHeight <= 768;

		const net = new Net();
		const localeQuery = net.getQueryString("locale");
		if (typeof localeQuery == "string") {
			this._locale = localeQuery;
		} else {
			if (window.frameElement != null) {
				const attribute = window.frameElement.attributes.getNamedItem("data-locale");
				if (attribute != null) {
					this._locale = attribute.value.toLowerCase();
				}
			}
		}

		this.scene.add("BootScene", BootScene, true);
		this.scene.add("RotateDevice", RotateDevice);
		this.scene.add("PreloaderScene", PreloaderScene);
		this.scene.add("ScreenTransition", ScreenTransition);

		this.scene.add("SplashScene", SplashScene);
		this.loadBatches.setBatches("SplashScene", "MenuBatches");
		this.scene.add("LevelSelectScene", LevelSelectScene);
		this.loadBatches.setBatches("LevelSelectScene", "MenuBatches");
		this.scene.add("GameCompleteScene", GameCompleteScene);
		this.loadBatches.setBatches("GameCompleteScene", "MenuBatches");

		this.scene.add("GameScene", GameScene);
		this.loadBatches.setBatches("GameScene", "GameplayBatches");
	}

	public step(time: number, delta: number): void {
		if (this._stats) this._stats.begin();
		super.step(time, delta);
		this._scaleManager.update();
		if (this._stats) this._stats.end();
	}
}
